'use client';

import { useState, useEffect, useCallback } from 'react';
import Header from '@/components/common/Header';
import KPIMetricCard from './KPIMetricCard';
import EnvironmentSelector from './EnvironmentSelector';
import ConversationFilterPanel from './ConversationFilterPanel';
import AutoRefreshToggle from './AutoRefreshToggle';
import ConversationFlowChart from './ConversationFlowChart';
import LiveActivityFeed from './LiveActivityFeed';
import AIAgentPerformanceGrid from './AIAgentPerformanceGrid';

interface Environment {
  id: string;
  name: string;
  status: 'active' | 'maintenance' | 'offline';
}

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface ConversationDataPoint {
  time: string;
  positive: number;
  neutral: number;
  negative: number;
  total: number;
}

interface ActivityItem {
  id: string;
  type: 'escalation' | 'knowledge_gap' | 'system_alert';
  severity: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  timestamp: string;
  actionLabel?: string;
}

interface AIAgent {
  id: string;
  name: string;
  status: 'active' | 'idle' | 'maintenance';
  activeConversations: number;
  avgConfidence: number;
  responseTime: number;
  successRate: number;
  handoverRate: number;
}

const OperationsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState('production');
  const [selectedChannels, setSelectedChannels] = useState<string[]>(['chat', 'email', 'slack']);
  const [selectedSentiments, setSelectedSentiments] = useState<string[]>(['positive', 'neutral', 'negative']);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const environments: Environment[] = [
    { id: 'production', name: 'Production', status: 'active' },
    { id: 'staging', name: 'Staging', status: 'active' },
    { id: 'development', name: 'Development', status: 'maintenance' }
  ];

  const channels: FilterOption[] = [
    { id: 'chat', label: 'Chat Widget', count: 342 },
    { id: 'email', label: 'Email', count: 156 },
    { id: 'slack', label: 'Slack', count: 89 },
    { id: 'zendesk', label: 'Zendesk', count: 67 }
  ];

  const sentiments: FilterOption[] = [
    { id: 'positive', label: 'Positive', count: 412 },
    { id: 'neutral', label: 'Neutral', count: 178 },
    { id: 'negative', label: 'Negative', count: 64 }
  ];

  const kpiData = [
    {
      title: 'Active Conversations',
      value: '342',
      change: 12.5,
      trend: 'up' as const,
      icon: 'ChatBubbleLeftRightIcon',
      sparklineData: [280, 295, 310, 305, 320, 335, 342],
      threshold: 'good' as const
    },
    {
      title: 'Avg Response Time',
      value: '1.8s',
      change: -8.3,
      trend: 'down' as const,
      icon: 'ClockIcon',
      sparklineData: [2.4, 2.2, 2.0, 1.9, 1.85, 1.82, 1.8],
      threshold: 'good' as const
    },
    {
      title: 'AI Confidence',
      value: '94.2%',
      change: 3.1,
      trend: 'up' as const,
      icon: 'SparklesIcon',
      sparklineData: [89, 90, 91.5, 92, 93, 93.5, 94.2],
      threshold: 'good' as const
    },
    {
      title: 'Handover Rate',
      value: '4.7%',
      change: -15.2,
      trend: 'down' as const,
      icon: 'ArrowsRightLeftIcon',
      sparklineData: [6.2, 5.8, 5.5, 5.2, 4.9, 4.8, 4.7],
      threshold: 'good' as const
    },
    {
      title: 'Knowledge Gaps',
      value: '12',
      change: 25.0,
      trend: 'up' as const,
      icon: 'ExclamationTriangleIcon',
      sparklineData: [8, 9, 10, 9, 11, 11, 12],
      threshold: 'warning' as const
    },
    {
      title: 'System Health',
      value: '99.8%',
      change: 0.2,
      trend: 'up' as const,
      icon: 'CheckCircleIcon',
      sparklineData: [99.5, 99.6, 99.7, 99.6, 99.7, 99.8, 99.8],
      threshold: 'good' as const
    }
  ];

  const conversationData: ConversationDataPoint[] = [
    { time: '09:00', positive: 45, neutral: 20, negative: 8, total: 73 },
    { time: '10:00', positive: 52, neutral: 25, negative: 10, total: 87 },
    { time: '11:00', positive: 58, neutral: 28, negative: 12, total: 98 },
    { time: '12:00', positive: 48, neutral: 22, negative: 9, total: 79 },
    { time: '13:00', positive: 55, neutral: 30, negative: 11, total: 96 },
    { time: '14:00', positive: 62, neutral: 32, negative: 13, total: 107 },
    { time: '15:00', positive: 68, neutral: 35, negative: 15, total: 118 },
    { time: '16:00', positive: 72, neutral: 38, negative: 14, total: 124 },
    { time: '17:00', positive: 65, neutral: 33, negative: 12, total: 110 }
  ];

  const activities: ActivityItem[] = [
    {
      id: '1',
      type: 'escalation',
      severity: 'high',
      title: 'Complex Billing Query Escalated',
      description: 'Customer requesting refund for annual subscription - requires manager approval',
      timestamp: '2 min ago',
      actionLabel: 'Review Case'
    },
    {
      id: '2',
      type: 'knowledge_gap',
      severity: 'medium',
      title: 'Missing Documentation Detected',
      description: 'AI agent unable to answer questions about new API v3 endpoints',
      timestamp: '5 min ago',
      actionLabel: 'Add Content'
    },
    {
      id: '3',
      type: 'system_alert',
      severity: 'low',
      title: 'High Confidence Threshold Reached',
      description: 'Agent "Support-AI-01" maintaining 98% confidence across 50+ conversations',
      timestamp: '8 min ago'
    },
    {
      id: '4',
      type: 'escalation',
      severity: 'medium',
      title: 'Technical Integration Issue',
      description: 'Customer reporting webhook failures with Slack integration',
      timestamp: '12 min ago',
      actionLabel: 'Investigate'
    },
    {
      id: '5',
      type: 'knowledge_gap',
      severity: 'high',
      title: 'Recurring Question Pattern',
      description: '15 customers asked about GDPR compliance in last hour - no knowledge base article',
      timestamp: '15 min ago',
      actionLabel: 'Create Article'
    },
    {
      id: '6',
      type: 'system_alert',
      severity: 'low',
      title: 'Response Time Optimization',
      description: 'Average response time improved by 18% after recent model update',
      timestamp: '22 min ago'
    }
  ];

  const aiAgents: AIAgent[] = [
    {
      id: '1',
      name: 'Support-AI-01',
      status: 'active',
      activeConversations: 87,
      avgConfidence: 96.5,
      responseTime: 1.6,
      successRate: 94.2,
      handoverRate: 3.8
    },
    {
      id: '2',
      name: 'Support-AI-02',
      status: 'active',
      activeConversations: 92,
      avgConfidence: 94.8,
      responseTime: 1.7,
      successRate: 92.5,
      handoverRate: 4.2
    },
    {
      id: '3',
      name: 'Support-AI-03',
      status: 'active',
      activeConversations: 78,
      avgConfidence: 95.2,
      responseTime: 1.5,
      successRate: 93.8,
      handoverRate: 3.5
    },
    {
      id: '4',
      name: 'Support-AI-04',
      status: 'idle',
      activeConversations: 45,
      avgConfidence: 93.1,
      responseTime: 1.9,
      successRate: 91.2,
      handoverRate: 5.1
    },
    {
      id: '5',
      name: 'Support-AI-05',
      status: 'active',
      activeConversations: 40,
      avgConfidence: 92.7,
      responseTime: 2.0,
      successRate: 90.8,
      handoverRate: 5.8
    }
  ];

  const handleRefresh = useCallback(() => {
    console.log('Refreshing data...');
  }, []);

  const handleDrillDown = useCallback((dataPoint: ConversationDataPoint) => {
    console.log('Drill down into:', dataPoint);
  }, []);

  const handleActivityAction = useCallback((activityId: string) => {
    console.log('Action clicked for activity:', activityId);
  }, []);

  const handleAgentClick = useCallback((agentId: string) => {
    console.log('Agent clicked:', agentId);
  }, []);

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:278:6" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="278" data-component-end-line="291" data-component-file="OperationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <Header data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:279:8" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="279" data-component-end-line="279" data-component-file="OperationsInteractive.tsx" data-component-name="Header" data-component-content="%7B%22elementName%22%3A%22Header%22%7D" showDatePicker={false} showRealtimeStatus={true} />
        <main data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:280:8" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="280" data-component-end-line="290" data-component-file="OperationsInteractive.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22pt-24%20px-8%20pb-8%22%7D" className="pt-24 px-8 pb-8">
          <div data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:281:10" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="281" data-component-end-line="289" data-component-file="OperationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-6%22%7D" className="animate-pulse space-y-6">
            <div data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:282:12" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="282" data-component-end-line="282" data-component-file="OperationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-surface%2F40%20rounded-lg%20w-1%2F3%22%7D" className="h-12 bg-surface/40 rounded-lg w-1/3" />
            <div data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:283:12" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="283" data-component-end-line="287" data-component-file="OperationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20xl%3Agrid-cols-6%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
              {[...Array(6)].map((_, i) => (
                <div data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:285:16" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="285" data-component-end-line="285" data-component-file="OperationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-40%20bg-surface%2F40%20rounded-xl%22%7D" key={i} className="h-40 bg-surface/40 rounded-xl" />
              ))}
            </div>
            <div data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:288:12" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="288" data-component-end-line="288" data-component-file="OperationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-surface%2F40%20rounded-xl%22%7D" className="h-96 bg-surface/40 rounded-xl" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:296:4" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="296" data-component-end-line="347" data-component-file="OperationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <Header data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:297:6" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="297" data-component-end-line="297" data-component-file="OperationsInteractive.tsx" data-component-name="Header" data-component-content="%7B%22elementName%22%3A%22Header%22%7D" showDatePicker={false} showRealtimeStatus={true} />
      
      <main data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:299:6" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="299" data-component-end-line="346" data-component-file="OperationsInteractive.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22pt-24%20px-8%20pb-8%22%7D" className="pt-24 px-8 pb-8">
        <div data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:300:8" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="300" data-component-end-line="307" data-component-file="OperationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
          <h1 data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:301:10" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="301" data-component-end-line="303" data-component-file="OperationsInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-4xl%20font-heading%20font-semibold%20text-foreground%20mb-2%22%2C%22textContent%22%3A%22Operations%20Command%20Center%22%7D" className="text-4xl font-heading font-semibold text-foreground mb-2">
            Operations Command Center
          </h1>
          <p data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:304:10" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="304" data-component-end-line="306" data-component-file="OperationsInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%22%2C%22textContent%22%3A%22Real-time%20monitoring%20and%20control%20of%20AI%20agent%20performance%20and%20customer%20interactions%22%7D" className="text-text-secondary">
            Real-time monitoring and control of AI agent performance and customer interactions
          </p>
        </div>

        <div data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:309:8" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="309" data-component-end-line="328" data-component-file="OperationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20items-center%20gap-4%20mb-8%22%7D" className="flex flex-wrap items-center gap-4 mb-8">
          <EnvironmentSelector data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:310:10" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="310" data-component-end-line="314" data-component-file="OperationsInteractive.tsx" data-component-name="EnvironmentSelector" data-component-content="%7B%22elementName%22%3A%22EnvironmentSelector%22%7D"
            environments={environments}
            selectedEnvironment={selectedEnvironment}
            onEnvironmentChange={setSelectedEnvironment}
          />
          
          <div data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:316:10" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="316" data-component-end-line="325" data-component-file="OperationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20min-w-%5B300px%5D%22%7D" className="flex-1 min-w-[300px]">
            <ConversationFilterPanel data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:317:12" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="317" data-component-end-line="324" data-component-file="OperationsInteractive.tsx" data-component-name="ConversationFilterPanel" data-component-content="%7B%22elementName%22%3A%22ConversationFilterPanel%22%7D"
              channels={channels}
              sentiments={sentiments}
              selectedChannels={selectedChannels}
              selectedSentiments={selectedSentiments}
              onChannelChange={setSelectedChannels}
              onSentimentChange={setSelectedSentiments}
            />
          </div>

          <AutoRefreshToggle data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:327:10" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="327" data-component-end-line="327" data-component-file="OperationsInteractive.tsx" data-component-name="AutoRefreshToggle" data-component-content="%7B%22elementName%22%3A%22AutoRefreshToggle%22%7D" interval={30000} onRefresh={handleRefresh} />
        </div>

        <div data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:330:8" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="330" data-component-end-line="334" data-component-file="OperationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20xl%3Agrid-cols-6%20gap-6%20mb-8%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
          {kpiData.map((kpi, index) => (
            <KPIMetricCard data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:332:12" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="332" data-component-end-line="332" data-component-file="OperationsInteractive.tsx" data-component-name="KPIMetricCard" data-component-content="%7B%22elementName%22%3A%22KPIMetricCard%22%2C%22%5Bspread%5D%22%3A%22true%22%7D" key={index} {...kpi} />
          ))}
        </div>

        <div data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:336:8" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="336" data-component-end-line="343" data-component-file="OperationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20xl%3Agrid-cols-3%20gap-6%20mb-8%22%7D" className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
          <div data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:337:10" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="337" data-component-end-line="339" data-component-file="OperationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22xl%3Acol-span-2%22%7D" className="xl:col-span-2">
            <ConversationFlowChart data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:338:12" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="338" data-component-end-line="338" data-component-file="OperationsInteractive.tsx" data-component-name="ConversationFlowChart" data-component-content="%7B%22elementName%22%3A%22ConversationFlowChart%22%7D" data={conversationData} onDrillDown={handleDrillDown} />
          </div>
          <div data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:340:10" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="340" data-component-end-line="342" data-component-file="OperationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22xl%3Acol-span-1%22%7D" className="xl:col-span-1">
            <LiveActivityFeed data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:341:12" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="341" data-component-end-line="341" data-component-file="OperationsInteractive.tsx" data-component-name="LiveActivityFeed" data-component-content="%7B%22elementName%22%3A%22LiveActivityFeed%22%7D" activities={activities} onActionClick={handleActivityAction} />
          </div>
        </div>

        <AIAgentPerformanceGrid data-component-id="src/app/operations-command-center/components/OperationsInteractive.tsx:345:8" data-component-path="src/app/operations-command-center/components/OperationsInteractive.tsx" data-component-line="345" data-component-end-line="345" data-component-file="OperationsInteractive.tsx" data-component-name="AIAgentPerformanceGrid" data-component-content="%7B%22elementName%22%3A%22AIAgentPerformanceGrid%22%7D" agents={aiAgents} onAgentClick={handleAgentClick} />
      </main>
    </div>
  );
};

export default OperationsInteractive;